// Local Storage Utilities for BMI History

const STORAGE_KEY = 'bmi_calculator_history';

/**
 * Get all stored BMI records
 */
export const getHistory = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return [];
  }
};

/**
 * Save a new BMI record
 */
export const saveRecord = (record) => {
  try {
    const history = getHistory();
    const newRecord = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      ...record
    };
    history.unshift(newRecord); // Add to beginning
    
    // Keep only last 50 records
    const trimmedHistory = history.slice(0, 50);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedHistory));
    return newRecord;
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    return null;
  }
};

/**
 * Delete a specific record by ID
 */
export const deleteRecord = (id) => {
  try {
    const history = getHistory();
    const filtered = history.filter(record => record.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Error deleting from localStorage:', error);
    return false;
  }
};

/**
 * Clear all history
 */
export const clearHistory = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
};

/**
 * Get trend data for charts
 */
export const getTrendData = () => {
  const history = getHistory();
  
  // Return last 10 records, reversed to show chronologically
  return history.slice(0, 10).reverse().map(record => ({
    date: new Date(record.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    bmi: record.bmi,
    weight: record.weight,
    waistRatio: record.waistToHeightRatio || null
  }));
};

/**
 * Get statistics from history
 */
export const getStatistics = () => {
  const history = getHistory();
  
  if (history.length === 0) {
    return null;
  }
  
  const bmis = history.map(r => r.bmi);
  const weights = history.map(r => r.weight);
  
  const avgBmi = bmis.reduce((a, b) => a + b, 0) / bmis.length;
  const avgWeight = weights.reduce((a, b) => a + b, 0) / weights.length;
  const minBmi = Math.min(...bmis);
  const maxBmi = Math.max(...bmis);
  const minWeight = Math.min(...weights);
  const maxWeight = Math.max(...weights);
  
  // Calculate trend (comparing last 3 to previous 3)
  let trend = 'stable';
  if (history.length >= 6) {
    const recentAvg = bmis.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
    const previousAvg = bmis.slice(3, 6).reduce((a, b) => a + b, 0) / 3;
    const diff = recentAvg - previousAvg;
    if (diff > 0.3) trend = 'increasing';
    else if (diff < -0.3) trend = 'decreasing';
  }
  
  return {
    totalRecords: history.length,
    avgBmi: avgBmi.toFixed(1),
    avgWeight: avgWeight.toFixed(1),
    minBmi: minBmi.toFixed(1),
    maxBmi: maxBmi.toFixed(1),
    minWeight: minWeight.toFixed(1),
    maxWeight: maxWeight.toFixed(1),
    trend,
    firstRecord: history[history.length - 1]?.date,
    lastRecord: history[0]?.date
  };
};
